import { Link } from "@/lib/router-compat";
import {
  Phone, ArrowRight, Star, ChevronDown, MapPin,
  Factory, Landmark, Heart, Warehouse, GraduationCap, Hotel, Building2, Home,
} from "lucide-react";
import HeadPreload from "@/components/HeadPreload";
import Layout from "@/components/Layout";
import PageSEO from "@/components/PageSEO";
import ScrollReveal from "@/components/ScrollReveal";
import HomepageHero from "@/components/HomepageHero";
import ContactForm from "@/components/ContactForm";
import { COMPANY } from "@/lib/seo";
import {
  homepageFaqs,
  homepageFork,
  homepageIndustries,
  homepageServiceAreas,
  homepageServiceCards,
} from "@/data/homepage-content";
import { homepageSchemas } from "@/data/route-schemas";
import { useState } from "react";

import freedomHall640Avif from "@/assets/freedom-hall-640.avif";
import freedomHall1000Avif from "@/assets/freedom-hall-1000.avif";
import freedomHall from "@/assets/freedom-hall.webp";
import churchillDowns from "@/assets/churchill-downs.webp";
import kccConventionCenter from "@/assets/kcc-convention-center.webp";
import uoflResearchLabs from "@/assets/uofl-research-labs.webp";
import hardinCountyWater from "@/assets/hardin-county-water.webp";
import manufacturingFloor from "@/assets/manufacturing-floor.webp";
import residentialHvac from "@/assets/residential-hvac.webp";
import electricalPanel from "@/assets/electrical-panel.webp";
import hvacDuctwork1 from "@/assets/hvac-ductwork-1.webp";
import lightingRetrofit from "@/assets/lighting-retrofit.webp";
import footballStadium from "@/assets/football-stadium.webp";

/* Kept intact — the four homepage Service JSON-LD nodes mirror these hubs.
   Copy and links live in src/data/homepage-content.ts (shared with the
   prerenderer); only the imagery is bound here. */
const serviceCardImages: Record<string, { img: string }> = {
  "/electrical-services": { img: electricalPanel },
  "/hvac-services": { img: hvacDuctwork1 },
  "/energy-management": { img: lightingRetrofit },
  "/stadium-sports-lighting": { img: footballStadium },
};
const serviceGroups = homepageServiceCards.map(card => ({ ...card, img: serviceCardImages[card.href]!.img }));

const industryIcons: Record<string, typeof Factory> = {
  "/industries/manufacturing-electrical": Factory,
  "/stadium-sports-lighting": Landmark,
  "/industries/healthcare-facility-compliance": Heart,
  "/industries/warehouse-electrical": Warehouse,
  "/industries/school-electrical-contractor": GraduationCap,
  "/industries/hotel-electrical-contractor": Hotel,
  "/industries/municipal-electrical": Building2,
  "/hvac-services/residential-hvac": Home,
};
const industries = homepageIndustries.map(i => ({ ...i, icon: industryIcons[i.href]! }));

const forkImages: Record<string, string> = {
  "/electrical-services": manufacturingFloor,
  "/hvac-services/residential-hvac": residentialHvac,
};
const forkCards = homepageFork.map(card => ({ ...card, img: forkImages[card.href] }));

const projects = [
  { img: kccConventionCenter, title: "Kentucky International Convention Center", scope: "Installation of non-proprietary temperature controls system (2018).", feature: true },
  { img: churchillDowns, title: "Churchill Downs", scope: "Electrical installation for the Churchill Downs retail store and surrounding facilities." },
  { img: freedomHall, title: "Freedom Hall, Louisville", scope: "Complete electrical service and lighting, including event power distribution." },
  { img: uoflResearchLabs, title: "University of Louisville Research Labs", scope: "New LED lighting with ABS lighting control system. Completed in less than two months." },
  { img: hardinCountyWater, title: "Hardin County Water Treatment Plant", scope: "Complete electrical and controls installation (2015)." },
];

const stats = [
  { value: "500+", label: "Projects Completed" },
  { value: "100+", label: "Years Combined Experience" },
  { value: "13+", label: "Years in Business" },
  { value: "24/7", label: "Emergency Response" },
];

const credentials = [
  { t: "Kentucky Contractor — License CE64623", d: "Issued by the Kentucky Department of Housing, Buildings & Construction." },
  { t: "Kentucky Master Electrician — License ME64500", d: "Founder Rodney Hadley holds the Kentucky master electrician credential personally." },
  { t: "Kentucky HVAC Master — License HM06538", d: "Required for commercial and residential HVAC installation, repair, and refrigerant work." },
  { t: "Associated Builders & Contractors of Indiana / Kentucky (ABC-IK)", d: "Active member in good standing." },
  { t: "Energy Efficiency Association", d: "Member contractor — commercial energy auditing and lighting retrofit work." },
  { t: "Certified Energy Auditing Company — Energy Audit Institute", d: "Certified to deliver ASHRAE Level I & II energy audits." },
  { t: "Generac Authorized Generator Installer", d: "Factory-trained on Generac standby and commercial generators." },
  { t: "Distech Controls & Schneider EcoStruxure Partner", d: "Authorized integrator for building automation and HVAC controls." },
  { t: "1-Year Workmanship Warranty (Written)", d: "Every electrical, HVAC, and lighting installation carries a written 1-year warranty." },
];

const testimonials = [
  { name: "John R.", text: "Service was top notch. One of the best company's I have had the pleasure of doing business with. The work crew is full of energetic knowledgeable employees. I would recommend them to anyone." },
  { name: "Debbie S.", text: "They were very responsive and professional. After being without power for 3 days, they had us up and running within 24 hours of calling them. EXCELLENT SERVICE!!!" },
  { name: "Malachi H.", text: "I had some electric work that I needed completed. Hadley Energy Solutions went above and beyond what I was expecting. Top notch service, top notch company. Would definitely recommend!" },
];

// Top priority service areas (rest are reachable via /service-area to keep crawl-graph clean)
const serviceAreas = homepageServiceAreas;

const faqs = homepageFaqs;

const Homepage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <Layout>
      <PageSEO
        title="Electrical & HVAC Company Louisville KY | Hadley"
        description="Trusted Louisville, KY electrical & HVAC contractor. Commercial, industrial & residential service, installs, and 24/7 emergency repair. Call today."
        canonical="/"
        // Built from src/data/route-schemas.ts so the prerendered static
        // JSON-LD is byte-identical to what the browser gets.
        // reviewSchema() intentionally omitted — it emits a second, weaker
        // local-business node (bare Electrician, no @id). The sitewide
        // GlobalSchema owns the canonical #localbusiness entity.
        schemas={homepageSchemas()}
      />

      <HeadPreload
        links={[
          { href: freedomHall640Avif, as: "image", type: "image/avif", media: "(max-width: 767px)", fetchpriority: "high", imagesrcset: `${freedomHall640Avif} 640w`, imagesizes: "100vw" },
          { href: freedomHall1000Avif, as: "image", type: "image/avif", media: "(min-width: 768px)", fetchpriority: "high", imagesrcset: `${freedomHall1000Avif} 1000w`, imagesizes: "100vw" },
        ]}
      />

      <HomepageHero />

      {/* ═══════════ §3 — THE FORK ═══════════ */}
      <section aria-label="Choose your path" className="bg-background">
        <div className="container py-10 md:py-12">
          <div className="grid gap-4 md:grid-cols-2">
            {forkCards.map(card => (
              <Link key={card.href} to={card.href} className="group relative block overflow-hidden rounded-[4px] transition-transform duration-150 hover:-translate-y-0.5">
                <img src={card.img} alt={card.alt} width="800" height="500" loading="lazy" decoding="async" className="h-[260px] w-full object-cover md:h-[320px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/45 to-charcoal/10" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <h2 className="font-heading text-2xl font-extrabold tracking-[-0.02em] text-white md:text-[2rem]">{card.title}</h2>
                  <p className="mt-2 max-w-[46ch] font-body text-[15px] leading-relaxed text-white/85">{card.line}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary-light">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ §4 — SERVICES ═══════════ */}
      <section className="border-y border-border bg-background">
        <div className="container py-10 md:py-12">
          <ScrollReveal>
            <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">What We Do</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">Full-Service Electrical, HVAC &amp; Energy Solutions</h2>
          </ScrollReveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {serviceGroups.map(group => (
              <ScrollReveal key={group.href}>
                <article className="flex h-full flex-col overflow-hidden rounded-[4px] border border-border bg-card transition-transform duration-150 hover:-translate-y-0.5">
                  <img src={group.img} alt={group.alt} width="600" height="380" loading="lazy" decoding="async" className="h-[180px] w-full object-cover" />
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-lg font-bold">
                      <Link to={group.href} className="hover:text-primary">{group.title}</Link>
                    </h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">{group.desc}</p>
                    <ul className="mt-4 space-y-1.5 border-t border-border pt-4">
                      {group.links.map(l => (
                        <li key={l.href}>
                          <Link to={l.href} className="font-body text-sm text-foreground hover:text-primary hover:underline">{l.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ §5 — SELECTED WORK ═══════════ */}
      <section className="bg-background">
        <div className="container py-10 md:py-12">
          <ScrollReveal>
            <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Selected Work</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">Our Work Speaks for Itself</h2>
          </ScrollReveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {projects.map(project => (
              <Link
                key={project.title}
                to="/portfolio"
                className={`group relative block overflow-hidden rounded-[4px] transition-transform duration-150 hover:-translate-y-0.5 ${project.feature ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""}`}
              >
                <img
                  src={project.img}
                  alt={`${project.title} — ${project.scope}`}
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className={`w-full object-cover ${project.feature ? "h-[260px] lg:h-[440px]" : "h-[220px] lg:h-[212px]"}`}
                />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-charcoal/92 to-transparent" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="font-heading text-[17px] font-bold leading-tight text-white">{project.title}</h3>
                  <p className="mt-1 font-body text-[13px] leading-snug text-white/75">{project.scope}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/portfolio" className="btn-primary px-6 py-3 text-sm">See Full Portfolio <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* ═══════════ §6 — PROOF ═══════════ */}
      <section className="bg-charcoal text-charcoal-foreground">
        <div className="container py-10 md:py-12">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {stats.map(stat => (
              <div key={stat.label}>
                <p className="font-condensed text-5xl font-bold leading-none text-white md:text-[64px]">{stat.value}</p>
                <p className="mt-2 font-body text-sm text-charcoal-foreground/70">{stat.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-[68ch] font-body text-[15px] leading-relaxed text-charcoal-foreground/70">
            Hadley Energy Solutions has called Louisville home since {COMPANY.founded}. Our shop on Old Preston
            Highway puts crews on the road fast across Jefferson County, the surrounding Kentucky cities, and
            Southern Indiana.
          </p>

          <h2 className="mt-10 font-heading text-2xl font-extrabold tracking-[-0.02em] text-white md:text-[1.75rem]">
            Licensed, Insured &amp; Independently Recognized in Louisville, KY
          </h2>
          <ul className="mt-5 grid gap-x-12 md:grid-cols-2">
            {credentials.map(item => (
              <li key={item.t} className="border-t border-charcoal-foreground/12 py-3">
                <p className="font-heading text-[15px] font-bold text-white">{item.t}</p>
                <p className="mt-1 font-body text-sm leading-relaxed text-charcoal-foreground/70">{item.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══════════ §6b — EMERGENCY BAND ═══════════ */}
      <section className="border-l-4 border-signal bg-charcoal text-charcoal-foreground">
        <div className="container flex min-h-24 flex-col items-start justify-between gap-4 py-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-xl font-bold text-white md:text-2xl">24/7 Emergency Electrical &amp; HVAC Service</h2>
            <p className="mt-1 font-body text-sm text-charcoal-foreground/70">
              Electrical issues don't wait — and neither do we. Our team provides rapid response when your business needs it most.
            </p>
          </div>
          <a href={`tel:${COMPANY.phoneTel}`} data-cta="emergency-band" className="btn-primary shrink-0 px-6 py-3 text-sm">
            <Phone className="h-4 w-4" /> Call {COMPANY.phone}
          </a>
        </div>
      </section>

      {/* ═══════════ §7 — INDUSTRIES ═══════════ */}
      <section className="bg-background">
        <div className="container py-10 md:py-12">
          <ScrollReveal>
            <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Who We Serve</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">Trusted by Louisville's Largest Organizations</h2>
          </ScrollReveal>
          <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 lg:grid-cols-4">
            {industries.map(ind => (
              <Link key={ind.title + ind.href} to={ind.href} className="group border-t border-border pt-4">
                <ind.icon className="h-5 w-5 text-primary" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-2 font-heading text-[15px] font-bold group-hover:text-primary">{ind.title}</h3>
                <p className="mt-1 font-body text-[13px] leading-relaxed text-muted-foreground">{ind.desc}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/industries" className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary hover:underline">View All Industries</Link>
          </div>
        </div>
      </section>

      {/* ═══════════ §8 — TESTIMONIALS ═══════════ */}
      <section className="border-y border-border bg-background">
        <div className="container py-10 md:py-12">
          <ScrollReveal>
            <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Customer Reviews</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">What Our Customers Say</h2>
          </ScrollReveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {testimonials.map(review => (
              <figure key={review.name} className="rounded-[4px] border border-border bg-card p-6">
                <div className="flex gap-0.5" aria-label="5 star rating">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-4 w-4 fill-primary text-primary" aria-hidden="true" />)}
                </div>
                <blockquote className="mt-4 font-body text-[15px] leading-relaxed text-foreground/90">"{review.text}"</blockquote>
                <figcaption className="mt-4 font-heading text-sm font-bold">— {review.name}</figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/reviews" className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary hover:underline">Read All Reviews</Link>
            <a href={COMPANY.googleReviews} target="_blank" rel="noopener noreferrer" className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary hover:underline">Read More Reviews on Google</a>
          </div>
        </div>
      </section>

      {/* ═══════════ §9 — ENERGY SAVINGS ═══════════ */}
      <section className="bg-primary text-primary-foreground">
        <div className="container py-14 text-center md:py-20">
          <h2 className="font-heading text-2xl font-extrabold tracking-[-0.02em] sm:text-3xl md:text-4xl">Save Up to 40% on Commercial Energy Bills</h2>
          <p className="mx-auto mt-4 max-w-3xl font-body text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
            Our in-house Energy Management Division delivers certified energy audits, LED lighting retrofits, and guaranteed energy savings financing — with no money out of pocket.
          </p>
          <Link to="/energy-management/energy-audits" className="mt-8 inline-flex items-center gap-2 rounded-[4px] bg-white px-7 py-3.5 font-heading text-base font-bold text-primary transition-opacity hover:opacity-90">
            Schedule a Free Energy Audit <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* ═══════════ §10 — SERVICE AREA ═══════════ */}
      <section className="bg-background">
        <div className="container py-10 md:py-12">
          <ScrollReveal>
            <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Where We Work</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">Serving Louisville &amp; Beyond</h2>
          </ScrollReveal>
          <ul className="mt-8 grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
            {serviceAreas.map(area => (
              <li key={area.slug} className="border-b border-border py-2">
                <Link to={`/service-area/${area.slug}`} className="inline-flex items-center gap-2 font-body text-[15px] hover:text-primary">
                  <MapPin className="h-4 w-4 text-primary" strokeWidth={1.5} aria-hidden="true" /> {area.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link to="/service-area" className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary hover:underline">View All Service Areas</Link>
          </div>
        </div>
      </section>

      {/* ═══════════ §11 — FAQ ═══════════ */}
      <section className="border-t border-border bg-background">
        <div className="container max-w-3xl py-10 md:py-12">
          <h2 className="font-heading text-3xl font-extrabold tracking-[-0.02em] md:text-[2.5rem]">Frequently Asked Questions</h2>
          <div className="mt-8 divide-y divide-border border-y border-border">
            {faqs.map((faq, i) => (
              <div key={faq.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-4 py-4 text-left font-heading text-[15px] font-bold"
                >
                  {faq.q}
                  <ChevronDown className={`h-5 w-5 shrink-0 text-primary transition-transform duration-150 ${openFaq === i ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openFaq === i && (
                  <p className="pb-5 font-body text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ §12 — FINAL CTA + SHORT FORM ═══════════ */}
      <section className="bg-charcoal text-charcoal-foreground">
        <div className="container grid gap-10 py-10 md:py-12 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-3xl font-extrabold tracking-[-0.02em] text-white md:text-[2.5rem]">Ready to Start Your Project?</h2>
            <p className="mt-4 max-w-[56ch] font-body text-[15px] leading-relaxed text-charcoal-foreground/70">
              Free estimates · Same-day emergency service · 1-year warranty on all work · Licensed &amp; insured
            </p>
            <p className="mt-4 max-w-[56ch] font-body text-[15px] leading-relaxed text-charcoal-foreground/70">
              Tell us about your project and we'll provide a detailed, no-obligation estimate. Most responses within one business day.
            </p>
            <div className="mt-8 space-y-3 font-body text-sm text-charcoal-foreground/80">
              <p><a href={`tel:${COMPANY.phoneTel}`} data-cta="final-cta" className="font-heading text-lg font-bold text-white hover:text-primary-light">{COMPANY.phone}</a></p>
              <p>{COMPANY.address}</p>
              <p>{COMPANY.hours}</p>
            </div>
          </div>
          <div className="rounded-[4px] bg-background p-6 text-foreground md:p-8">
            <ContactForm compact source="homepage_form" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Homepage;
